import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Form,
    Input,
    Modal,
    Popconfirm,
    Select,
    Space,
    Table,
    Tabs,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    BellOutlined,
    CheckOutlined,
    CloudSyncOutlined,
    ExperimentOutlined,
    RocketOutlined,
    CrownOutlined,
    ContactsOutlined,
    DeleteOutlined,
    MailOutlined,
    PlusOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
    useGetCrmCompaniesQuery,
    useGetCrmPipelineStagesQuery,
    useGetCrmLinkSuggestionsQuery,
    useGetCrmTasksQuery,
    useGetCrmSyncStatusQuery,
    useCreateCrmCompanyMutation,
    useUpdateCrmCompanyMutation,
    useUpdateCrmTaskMutation,
    useConfirmCrmLinkSuggestionMutation,
    useRejectCrmLinkSuggestionMutation,
    useRunCrmSyncMutation,
    useRunCrmLinkingMutation,
    useRunCrmHealthMutation,
    useRunCrmPostHogMutation,
} from '../../services/nestjs-base-api';
import { ICrmCompany, ICrmTask } from '../../services/nestjs-base-api/types';
import { useAuth } from '../../hooks/useAuth';
import { CrmKanban } from '../components/crm/CrmKanban';
import { CrmCompanyDrawer } from '../components/crm/CrmCompanyDrawer';
import { CrmReportsTab } from '../components/crm/CrmReportsTab';
import { CrmConciergeTab } from '../components/crm/CrmConciergeTab';
import { CrmCharterTab } from '../components/crm/CrmCharterTab';
import { CrmHypothesisTab } from '../components/crm/CrmHypothesisTab';
import { CrmInboxTab } from '../components/crm/CrmInboxTab';
import { CrmEmailTemplatesTab } from '../components/crm/CrmEmailTemplatesTab';
import { LifecycleTag, StageTag, SourceIcon, formatAest, isPast } from '../components/crm/crmUi';

const { Title, Text } = Typography;

export const AdminCrmPage: React.FC = () => {
    const intl = useIntl();
    const { user } = useAuth();
    const jwtToken = user?.jwtToken || '';

    const [searchParams, setSearchParams] = useSearchParams();
    const deepLinkedCompany = searchParams.get('company');

    const [q, setQ] = useState('');
    const [drawerCompanyId, setDrawerCompanyId] = useState<number | null>(
        deepLinkedCompany ? Number(deepLinkedCompany) : null,
    );
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [showArchived, setShowArchived] = useState(false);
    const [createForm] = Form.useForm();

    // Re-apply the ?company= deep link when it changes after mount (e.g. the
    // header reminder bell navigating while already on this page — SYR-95).
    useEffect(() => {
        if (deepLinkedCompany) setDrawerCompanyId(Number(deepLinkedCompany));
    }, [deepLinkedCompany]);

    const { data: stages } = useGetCrmPipelineStagesQuery({ jwtToken });
    const {
        data: companiesData,
        isLoading,
        isFetching,
        refetch,
    } = useGetCrmCompaniesQuery({
        jwtToken,
        q: q || undefined,
        includeArchived: showArchived || undefined,
    });
    const { data: suggestions } = useGetCrmLinkSuggestionsQuery({ jwtToken });
    const { data: tasks } = useGetCrmTasksQuery({ jwtToken, status: 'open' });
    const { data: syncStatus } = useGetCrmSyncStatusQuery({ jwtToken });

    const [createCompany] = useCreateCrmCompanyMutation();
    const [updateCompany] = useUpdateCrmCompanyMutation();
    const [updateTask] = useUpdateCrmTaskMutation();
    const [confirmSuggestion] = useConfirmCrmLinkSuggestionMutation();
    const [rejectSuggestion] = useRejectCrmLinkSuggestionMutation();
    const [runSync, runSyncResult] = useRunCrmSyncMutation();
    const [runLinking, runLinkingResult] = useRunCrmLinkingMutation();
    const [runHealth, runHealthResult] = useRunCrmHealthMutation();
    const [runPostHog, runPostHogResult] = useRunCrmPostHogMutation();

    const companies = useMemo(() => companiesData?.data || [], [companiesData]);
    const stageList = useMemo(() => stages || [], [stages]);

    const handleStageChange = async (company: ICrmCompany, stageId: number) => {
        const stage = stageList.find((s) => s.id === stageId);
        if (stage?.isLost) {
            // Lost needs a reason — route through the drawer where it is asked.
            setDrawerCompanyId(company.id);
            return;
        }
        try {
            await updateCompany({
                jwtToken,
                id: company.id,
                payload: { pipelineStageId: stageId },
            }).unwrap();
        } catch (e: any) {
            message.error(e?.data?.message || 'Failed to move account');
        }
    };

    const companyColumns: ColumnsType<ICrmCompany> = [
        {
            title: <FormattedMessage id="crm.column.name" defaultMessage="Account" />,
            dataIndex: 'name',
            key: 'name',
            fixed: 'left',
            sorter: (a, b) => a.name.localeCompare(b.name),
            render: (_, c) => (
                <Space size={6}>
                    <SourceIcon source={c.source} />
                    <a onClick={() => setDrawerCompanyId(c.id)}>{c.name}</a>
                </Space>
            ),
        },
        {
            title: <FormattedMessage id="crm.column.stage" defaultMessage="Stage" />,
            key: 'stage',
            filters: stageList.map((s) => ({ text: s.label, value: s.id })),
            onFilter: (value, c) => c.pipelineStage.id === value,
            render: (_, c) => <StageTag stage={c.pipelineStage} />,
        },
        {
            title: <FormattedMessage id="crm.column.lifecycle" defaultMessage="Lifecycle" />,
            key: 'lifecycle',
            render: (_, c) => <LifecycleTag stage={c.lifecycleStage} />,
        },
        {
            title: <FormattedMessage id="crm.column.contact" defaultMessage="Contact" />,
            key: 'contact',
            render: (_, c) =>
                c.primaryContact ? (
                    <>
                        <div>{c.primaryContact.name}</div>
                        {c.primaryContact.email && (
                            <a href={`mailto:${c.primaryContact.email}`} style={{ fontSize: 12 }}>
                                {c.primaryContact.email}
                            </a>
                        )}
                    </>
                ) : (
                    '—'
                ),
        },
        {
            title: <FormattedMessage id="crm.column.productType" defaultMessage="Product type" />,
            dataIndex: 'productType',
            key: 'productType',
            render: (v) => (v ? <Tag color="blue">{v}</Tag> : '—'),
        },
        {
            title: <FormattedMessage id="crm.column.owner" defaultMessage="Owner" />,
            dataIndex: 'ownerName',
            key: 'owner',
            render: (v) => v || '—',
        },
        {
            title: <FormattedMessage id="crm.column.lastActivity" defaultMessage="Last activity" />,
            key: 'lastActivity',
            sorter: (a, b) =>
                new Date(a.lastActivityAt || a.updatedAt).getTime() -
                new Date(b.lastActivityAt || b.updatedAt).getTime(),
            render: (_, c) =>
                c.lastActivityAt ? dayjs(c.lastActivityAt).format('YYYY-MM-DD') : '—',
        },
        {
            title: <FormattedMessage id="crm.column.tasks" defaultMessage="Tasks" />,
            dataIndex: 'openTaskCount',
            key: 'tasks',
            render: (v) => (v > 0 ? v : '—'),
        },
    ];

    const taskColumns: ColumnsType<ICrmTask> = [
        {
            title: <FormattedMessage id="crm.task.title" defaultMessage="Title" />,
            dataIndex: 'title',
            key: 'title',
            render: (_, t) => (
                <Space size={6}>
                    {t.title}
                    {t.reminderEnabled && (
                        <Tooltip
                            title={
                                <FormattedMessage
                                    id="crm.task.reminderTooltip"
                                    defaultMessage="Reminder: emails the creator when due"
                                />
                            }
                        >
                            <BellOutlined
                                style={{
                                    color:
                                        t.dueAt && isPast(t.dueAt) ? '#ff4d4f' : '#8c8c8c',
                                    fontSize: 12,
                                }}
                            />
                        </Tooltip>
                    )}
                </Space>
            ),
        },
        {
            title: <FormattedMessage id="crm.task.company" defaultMessage="Account" />,
            key: 'company',
            render: (_, t) => (
                <a onClick={() => setDrawerCompanyId(t.crmCompanyId)}>{t.companyName}</a>
            ),
        },
        {
            title: <FormattedMessage id="crm.task.dueAest" defaultMessage="Due (AEST)" />,
            key: 'due',
            sorter: (a, b) =>
                new Date(a.dueAt || '2999-01-01').getTime() -
                new Date(b.dueAt || '2999-01-01').getTime(),
            defaultSortOrder: 'ascend',
            render: (_, t) =>
                t.dueAt ? (
                    <Text type={isPast(t.dueAt) ? 'danger' : undefined}>
                        {formatAest(t.dueAt)}
                    </Text>
                ) : (
                    '—'
                ),
        },
        {
            title: <FormattedMessage id="crm.task.assignee" defaultMessage="Assignee" />,
            dataIndex: 'assignedToName',
            key: 'assignee',
            render: (v) => v || '—',
        },
        {
            title: '',
            key: 'actions',
            render: (_, t) => (
                <Space size={8}>
                    <Button
                        size="small"
                        icon={<CheckOutlined style={{ marginRight: 4 }} />}
                        onClick={() =>
                            updateTask({ jwtToken, id: t.id, payload: { status: 'done' } })
                        }
                    >
                        <FormattedMessage id="crm.task.markDone" defaultMessage="Done" />
                    </Button>
                    <Popconfirm
                        title={
                            <FormattedMessage
                                id="crm.task.deleteConfirm"
                                defaultMessage="Delete this task?"
                            />
                        }
                        onConfirm={() =>
                            updateTask({ jwtToken, id: t.id, payload: { status: 'cancelled' } })
                        }
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const syncRunColumns: ColumnsType<any> = [
        {
            title: <FormattedMessage id="crm.sync.started" defaultMessage="Started" />,
            dataIndex: 'startedAt',
            key: 'startedAt',
            render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
            title: <FormattedMessage id="crm.sync.status" defaultMessage="Status" />,
            dataIndex: 'status',
            key: 'status',
            render: (v) => (
                <Tag color={v === 'succeeded' ? 'green' : v === 'failed' ? 'red' : 'blue'}>{v}</Tag>
            ),
        },
        {
            title: <FormattedMessage id="crm.sync.rowsPulled" defaultMessage="Rows pulled" />,
            dataIndex: 'rowsPulled',
            key: 'rowsPulled',
        },
        {
            title: <FormattedMessage id="crm.sync.rowsCreated" defaultMessage="Created" />,
            dataIndex: 'rowsCreated',
            key: 'rowsCreated',
        },
        {
            title: <FormattedMessage id="crm.sync.rowsPushed" defaultMessage="Pushed" />,
            dataIndex: 'rowsPushed',
            key: 'rowsPushed',
        },
        {
            title: <FormattedMessage id="crm.sync.conflicts" defaultMessage="Conflicts" />,
            dataIndex: 'conflicts',
            key: 'conflicts',
            render: (v) => (v > 0 ? <Tag color="orange">{v}</Tag> : '—'),
        },
        {
            title: <FormattedMessage id="crm.sync.error" defaultMessage="Error" />,
            dataIndex: 'error',
            key: 'error',
            render: (v) => v || '—',
        },
    ];

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 12,
                    marginBottom: 16,
                }}
            >
                <div>
                    <Title level={3} style={{ margin: 0 }}>
                        <ContactsOutlined style={{ marginRight: 8 }} />
                        <FormattedMessage id="crm.title" defaultMessage="CRM" />
                    </Title>
                    <Text type="secondary">
                        <FormattedMessage
                            id="crm.subtitle"
                            defaultMessage="Pipeline, accounts and tasks — synced with the BSAI Customers sheet"
                        />
                    </Text>
                </div>
                <Space wrap>
                    <Input.Search
                        allowClear
                        placeholder={intl.formatMessage({ id: 'crm.searchPlaceholder' })}
                        style={{ width: 280 }}
                        onSearch={setQ}
                    />
                    <Checkbox checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)}>
                        <FormattedMessage id="crm.showArchived" defaultMessage="Archived" />
                    </Checkbox>
                    <Button icon={<ReloadOutlined />} loading={isFetching} onClick={() => refetch()} />
                    <Button
                        type="primary"
                        icon={<PlusOutlined style={{ marginRight: 4 }} />}
                        onClick={() => setCreateModalOpen(true)}
                    >
                        <FormattedMessage id="crm.newCompany" defaultMessage="New account" />
                    </Button>
                </Space>
            </div>

            {(suggestions || []).map((s) => (
                <Alert
                    key={`${s.prospectCompanyId}-${s.organisationId}`}
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={
                        <FormattedMessage
                            id="crm.suggestions.text"
                            defaultMessage='Prospect "{prospect}" looks like organisation "{org}" (matched by {matchType}: {matchValue})'
                            values={{
                                prospect: s.prospectCompanyName,
                                org: s.organisationName,
                                matchType: s.matchType,
                                matchValue: s.matchValue,
                            }}
                        />
                    }
                    action={
                        <Space>
                            <Button
                                size="small"
                                type="primary"
                                onClick={async () => {
                                    try {
                                        await confirmSuggestion({
                                            jwtToken,
                                            prospectCompanyId: s.prospectCompanyId,
                                            signupCompanyId: s.signupCompanyId,
                                        }).unwrap();
                                        message.success(
                                            intl.formatMessage({ id: 'crm.suggestions.linked' }),
                                        );
                                    } catch (e: any) {
                                        message.error(e?.data?.message || 'Failed to link');
                                    }
                                }}
                            >
                                <FormattedMessage id="crm.suggestions.confirm" defaultMessage="Link" />
                            </Button>
                            <Button
                                size="small"
                                onClick={() =>
                                    rejectSuggestion({
                                        jwtToken,
                                        prospectCompanyId: s.prospectCompanyId,
                                        organisationId: s.organisationId,
                                    })
                                }
                            >
                                <FormattedMessage id="crm.suggestions.reject" defaultMessage="Dismiss" />
                            </Button>
                        </Space>
                    }
                />
            ))}

            <Tabs
                defaultActiveKey={searchParams.get('tab') ?? 'pipeline'}
                items={[
                    {
                        key: 'pipeline',
                        label: <FormattedMessage id="crm.tab.pipeline" defaultMessage="Pipeline" />,
                        children: (
                            <CrmKanban
                                stages={stageList}
                                companies={companies.filter((c) => !c.isArchived)}
                                onCardClick={(c) => setDrawerCompanyId(c.id)}
                                onStageChange={handleStageChange}
                            />
                        ),
                    },
                    {
                        key: 'companies',
                        label: <FormattedMessage id="crm.tab.companies" defaultMessage="Companies" />,
                        children: (
                            <Table
                                rowKey="id"
                                size="small"
                                loading={isLoading}
                                dataSource={companies}
                                columns={companyColumns}
                                scroll={{ x: 'max-content' }}
                                pagination={{ pageSize: 25, showSizeChanger: true }}
                            />
                        ),
                    },
                    {
                        key: 'tasks',
                        label: (
                            <span>
                                <FormattedMessage id="crm.tab.tasks" defaultMessage="Tasks" />
                                {(tasks || []).length > 0 ? ` (${(tasks || []).length})` : ''}
                            </span>
                        ),
                        children: (
                            <Table
                                rowKey="id"
                                size="small"
                                dataSource={tasks || []}
                                columns={taskColumns}
                                pagination={{ pageSize: 25 }}
                            />
                        ),
                    },
                    {
                        key: 'inbox',
                        label: (
                            <span>
                                <MailOutlined style={{ marginRight: 8 }} />
                                <FormattedMessage id="crm.tab.inbox" defaultMessage="Inbox" />
                            </span>
                        ),
                        children: <CrmInboxTab onOpenCompany={setDrawerCompanyId} />,
                    },
                    {
                        key: 'reports',
                        label: <FormattedMessage id="crm.tab.reports" defaultMessage="Reports" />,
                        children: <CrmReportsTab onOpenCompany={setDrawerCompanyId} />,
                    },
                    {
                        key: 'hypothesis',
                        label: (
                            <span>
                                <ExperimentOutlined style={{ marginRight: 8 }} />
                                <FormattedMessage
                                    id="crm.tab.hypothesis"
                                    defaultMessage="The Hypothesis"
                                />
                            </span>
                        ),
                        children: <CrmHypothesisTab />,
                    },
                    {
                        key: 'concierge',
                        label: (
                            <span>
                                <RocketOutlined style={{ marginRight: 8 }} />
                                <FormattedMessage
                                    id="crm.tab.concierge"
                                    defaultMessage="Auto-Concierge"
                                />
                            </span>
                        ),
                        children: <CrmConciergeTab />,
                    },
                    {
                        key: 'charter',
                        label: (
                            <span>
                                <CrownOutlined style={{ marginRight: 8 }} />
                                <FormattedMessage
                                    id="crm.tab.charter"
                                    defaultMessage="Charter"
                                />
                            </span>
                        ),
                        children: <CrmCharterTab />,
                    },
                    {
                        key: 'templates',
                        label: (
                            <span>
                                <MailOutlined style={{ marginRight: 8 }} />
                                <FormattedMessage id="crm.tab.templates" defaultMessage="Email Templates" />
                            </span>
                        ),
                        children: <CrmEmailTemplatesTab />,
                    },
                    {
                        key: 'sync',
                        label: (
                            <span>
                                <CloudSyncOutlined style={{ marginRight: 8 }} />
                                <FormattedMessage id="crm.tab.sync" defaultMessage="Sheet Sync" />
                            </span>
                        ),
                        children: (
                            <Card size="small">
                                <Space direction="vertical" style={{ width: '100%' }} size={16}>
                                    {!syncStatus?.configured && (
                                        <Alert
                                            type="error"
                                            showIcon
                                            message={
                                                <FormattedMessage
                                                    id="crm.sync.notConfigured"
                                                    defaultMessage="Google Sheets credentials are not configured on the API"
                                                />
                                            }
                                        />
                                    )}
                                    <Space wrap>
                                        <Tag color={syncStatus?.enabled ? 'green' : 'orange'}>
                                            {syncStatus?.enabled ? (
                                                <FormattedMessage
                                                    id="crm.sync.enabled"
                                                    defaultMessage="Auto-sync ON (every {minutes} min)"
                                                    // 0 means "every tick", and the job ticks every 5 minutes.
                                                    values={{ minutes: Math.max(syncStatus?.intervalMinutes ?? 60, 5) }}
                                                />
                                            ) : (
                                                <FormattedMessage id="crm.sync.disabled" defaultMessage="Auto-sync OFF (CRM_SHEET_SYNC_ENABLED)" />
                                            )}
                                        </Tag>
                                        <Button
                                            type="primary"
                                            loading={runSyncResult.isLoading}
                                            disabled={!syncStatus?.configured}
                                            onClick={async () => {
                                                const run = await runSync({ jwtToken }).unwrap();
                                                if (run.status === 'succeeded') {
                                                    message.success(
                                                        intl.formatMessage(
                                                            { id: 'crm.sync.completed' },
                                                            { pulled: run.rowsPulled, created: run.rowsCreated },
                                                        ),
                                                    );
                                                } else {
                                                    message.error(run.error || 'Sync failed');
                                                }
                                            }}
                                        >
                                            <FormattedMessage id="crm.sync.runNow" defaultMessage="Pull sheet now" />
                                        </Button>
                                        <Button
                                            loading={runLinkingResult.isLoading}
                                            onClick={async () => {
                                                const result = await runLinking({ jwtToken }).unwrap();
                                                message.success(
                                                    intl.formatMessage(
                                                        { id: 'crm.sync.linkingCompleted' },
                                                        { created: result.created, linked: result.linked },
                                                    ),
                                                );
                                            }}
                                        >
                                            <FormattedMessage id="crm.sync.runLinking" defaultMessage="Sync organisations now" />
                                        </Button>
                                        <Button
                                            loading={runHealthResult.isLoading}
                                            onClick={async () => {
                                                const result = await runHealth({ jwtToken }).unwrap();
                                                message.success(
                                                    intl.formatMessage(
                                                        { id: 'crm.sync.healthCompleted' },
                                                        { scored: result.scored, signals: result.signals },
                                                    ),
                                                );
                                            }}
                                        >
                                            <FormattedMessage id="crm.sync.runHealth" defaultMessage="Recompute health now" />
                                        </Button>
                                        <Button
                                            loading={runPostHogResult.isLoading}
                                            onClick={async () => {
                                                const result = await runPostHog({ jwtToken }).unwrap();
                                                message.success(
                                                    intl.formatMessage(
                                                        { id: 'crm.sync.postHogCompleted' },
                                                        { refreshed: result.refreshed },
                                                    ),
                                                );
                                            }}
                                        >
                                            <FormattedMessage id="crm.sync.runPostHog" defaultMessage="Refresh PostHog analytics" />
                                        </Button>
                                    </Space>
                                    <Table
                                        rowKey="id"
                                        size="small"
                                        dataSource={syncStatus?.lastRuns || []}
                                        columns={syncRunColumns}
                                        pagination={false}
                                    />
                                </Space>
                            </Card>
                        ),
                    },
                ]}
            />

            <CrmCompanyDrawer
                companyId={drawerCompanyId}
                stages={stageList}
                onClose={() => {
                    setDrawerCompanyId(null);
                    if (deepLinkedCompany) setSearchParams({}, { replace: true });
                }}
            />

            <Modal
                open={createModalOpen}
                title={<FormattedMessage id="crm.newCompany" defaultMessage="New account" />}
                onCancel={() => setCreateModalOpen(false)}
                onOk={() => createForm.submit()}
            >
                <Form
                    form={createForm}
                    layout="vertical"
                    onFinish={async (values) => {
                        try {
                            await createCompany({ jwtToken, payload: values }).unwrap();
                            createForm.resetFields();
                            setCreateModalOpen(false);
                        } catch (e: any) {
                            message.error(e?.data?.message || 'Failed to create account');
                        }
                    }}
                >
                    <Form.Item
                        name="name"
                        rules={[{ required: true }]}
                        label={<FormattedMessage id="crm.column.name" defaultMessage="Account" />}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item name="website" label={<FormattedMessage id="crm.create.website" defaultMessage="Website" />}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="productType" label={<FormattedMessage id="crm.column.productType" defaultMessage="Product type" />}>
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="pipelineStageId"
                        label={<FormattedMessage id="crm.column.stage" defaultMessage="Stage" />}
                    >
                        <Select
                            allowClear
                            options={stageList
                                .filter((s) => s.isActive)
                                .map((s) => ({ value: s.id, label: s.label }))}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
